import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  CardMedia,
  TextField,
  Stack,
  IconButton,
  Menu,
  MenuItem,
  AppBar,
  Toolbar,
  Tabs,
  Tab,
  Badge,
} from '@mui/material';
import { PostAdd } from '@mui/icons-material';
import { SnackbarProvider } from 'notistack';
import {
  Edit,
  Delete,
  PhotoLibrary,
  Person,
  SupervisorAccount,
  AccountCircle,
  ExitToApp,
  Add,
  Event,
  Notifications,
  AttachMoney,
  Schedule,
  MonetizationOn,
  CalendarToday,
  FormatQuote,
  AdminPanelSettings,
  Save,
  Route
} from '@mui/icons-material';
import { format } from 'date-fns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import axios from '../../utils/axiosConfig';
import TrainingManagement from './TrainingManagement';
import ManageGallery from './ManageGallery';
import TrainerManagement from './TrainerManagement';
import AdminManagement from './AdminManagement';
import TestimonialManagement from './TestimonialManagement';
import TrainingPathManagement from './TrainingPathManagement';
import GalleryForm from '../../components/GalleryForm';
import { DataGrid } from '@mui/x-data-grid';

const API_URL = 'https://api.narrowgatepath.com';

const Dashboard = () => {
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(0);
  const [blogPosts, setBlogPosts] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [blogToDelete, setBlogToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [stats, setStats] = useState({
    activeClients: 0,
    monthlyIncome: 0,
    upcomingSessions: 0
  });
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [activeClients, setActiveClients] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  // Gallery state
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryError, setGalleryError] = useState('');
  const [imageToDelete, setImageToDelete] = useState(null);
  const [deleteImageDialogOpen, setDeleteImageDialogOpen] = useState(false);
  const [addImageDialogOpen, setAddImageDialogOpen] = useState(false);

  // Main Image state
  const [mainImage, setMainImage] = useState(null);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [crop, setCrop] = useState({
    unit: '%',
    x: 0,
    y: 0,
    width: 90,
    height: 90
  });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const imageRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const fileInputRef = useRef(null);

  // Main Page Text state
  const [mainPageSettings, setMainPageSettings] = useState({
    title: '',
    subtitle: ''
  });
  const [isEditingText, setIsEditingText] = useState(false);
  const [editedSettings, setEditedSettings] = useState({
    title: '',
    subtitle: ''
  });

  // Image upload handlers
  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result);
        setCropDialogOpen(true);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error reading file:', error);
      setError('Failed to read image file');
    }
  };

  // Handle paste event
  useEffect(() => {
    const handlePaste = async (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      const imageItem = Array.from(items).find(item => item.type.startsWith('image'));
      if (!imageItem) return;

      try {
        const file = imageItem.getAsFile();
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => {
          setImageSrc(reader.result);
          setCropDialogOpen(true);
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Error handling paste:', error);
        setError('Failed to paste image');
      }
    };

    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, []);

  // Handle crop change
  const onCropChange = (crop, percentCrop) => {
    setCrop(percentCrop);
  };

  const onCropComplete = (crop, percentCrop) => {
    setCompletedCrop(crop);
  };

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    const crop = {
      unit: '%',
      x: 0,
      y: 0,
      width: 90,
      height: (90 * height) / width,
    };
    setCrop(crop);
  };

  // Bookings state
  const [selectedClientBookings, setSelectedClientBookings] = useState([]);
  const [bookingsModalOpen, setBookingsModalOpen] = useState(false);
  const [bookingsLoading, setBookingsLoading] = useState(false);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch all data in parallel
        const [postsRes, statsRes, sessionsRes, clientsRes, profileRes, mainImageRes, mainPageRes] = await Promise.all([
          axios.get('/api/v2/blog/admin'),
          axios.get('/api/v2/admin/stats'),
          axios.get('/api/v2/admin/upcoming-sessions'),
          axios.get('/api/v2/admin/clients'),
          axios.get('/api/v2/auth/me'),
          axios.get('/api/v2/settings/main-image'),
          axios.get('/api/v2/settings/main-page')
        ]);

        console.log('Stats data received:', statsRes.data);
        setBlogPosts(postsRes.data);
        setStats(statsRes.data.data || {
          activeClients: 0,
          monthlyIncome: 0,
          upcomingSessions: 0
        });
        setUpcomingSessions(sessionsRes.data.data || []);
        setActiveClients(clientsRes.data.clients);
        setIsSuperAdmin(profileRes.data.admin?.role === 'superadmin');
        setMainImage(mainImageRes.data);
        setMainPageSettings(mainPageRes.data);
        setEditedSettings(mainPageRes.data);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError('Failed to load dashboard data');
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Fetch gallery images
  const fetchGalleryImages = useCallback(async () => {
    setGalleryLoading(true);
    setGalleryError('');

    try {
      const response = await axios.get(`${API_URL}/api/v2/admin/gallery`);

      setGalleryImages(response.data);
    } catch (err) {
      console.error('Error fetching gallery:', err);
      setGalleryError('Failed to load gallery images');
    } finally {
      setGalleryLoading(false);
    }
  }, []);

  // Fetch upcoming sessions
  const fetchUpcomingSessions = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/v2/admin/upcoming-sessions`);
      console.log('Upcoming sessions response:', response.data);
      if (response.data.success) {
        setUpcomingSessions(response.data.data || []);
      } else {
        setError(response.data.message || 'Failed to fetch upcoming sessions');
      }
    } catch (error) {
      console.error('Error fetching upcoming sessions:', error);
      setError('Failed to fetch upcoming sessions');
    }
  }, []);

  useEffect(() => {
    fetchUpcomingSessions();
  }, [fetchUpcomingSessions]);

  // Handle logout
  const handleLogout = async () => {
    try {
      await axios.post(`${API_URL}/api/v2/auth/logout`);
      window.location.href = '/';
    } catch (err) {
      console.error('Logout error:', err);
      window.location.href = '/';
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // Handle menu
  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleViewBookings = async (clientId) => {
    try {
      setBookingsLoading(true);
      const response = await axios.get(`/api/v2/admin/bookings/${clientId}`);
      setSelectedClientBookings(response.data);
      setBookingsModalOpen(true);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setError('Failed to fetch bookings');
    } finally {
      setBookingsLoading(false);
    }
  };

  const handleConfirmPayment = async (bookingId) => {
    try {
      await axios.put(`/api/v2/admin/bookings/${bookingId}/confirm`);
      // Refresh the bookings data
      if (selectedClientBookings[0]?.client) {
        handleViewBookings(selectedClientBookings[0].client);
      }
    } catch (error) {
      console.error('Error confirming payment:', error);
      setError('Failed to confirm payment');
    }
  };

  const handleCloseBookingsModal = () => {
    setBookingsModalOpen(false);
    setSelectedClientBookings([]);
  };

  const handleSaveText = async () => {
    try {
      const response = await axios.put('/api/v2/settings/main-page', editedSettings);
      setMainPageSettings(response.data);
      setIsEditingText(false);
      setSuccessMessage('Main page text updated successfully');
    } catch (error) {
      console.error('Error updating main page text:', error);
      setError('Failed to update main page text');
    }
  };

  const handleCancelEdit = () => {
    setEditedSettings(mainPageSettings);
    setIsEditingText(false);
  };

  // Handle crop dialog close and upload
  const handleCloseCropDialog = () => {
    setCropDialogOpen(false);
    setImageSrc(null);
  };

  const handleUploadCroppedImage = async () => {
    if (!completedCrop || !imageRef.current) return;

    const canvas = document.createElement('canvas');
    const scaleX = imageRef.current.naturalWidth / imageRef.current.width;
    const scaleY = imageRef.current.naturalHeight / imageRef.current.height;

    canvas.width = completedCrop.width;
    canvas.height = completedCrop.height;
    const ctx = canvas.getContext('2d');

    if (!ctx) return;

    ctx.drawImage(
      imageRef.current,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      completedCrop.width,
      completedCrop.height
    );

    try {
      setIsImageUploading(true);
      
      // Convert canvas to blob
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to create blob'));
              return;
            }
            blob.name = 'main-image.jpg';
            resolve(blob);
          },
          'image/jpeg',
          0.95
        );
      });

      const formData = new FormData();
      formData.append('image', blob, 'main-image.jpg');

      const response = await axios.put('/api/v2/settings/main-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setMainImage(response.data);
      setSuccessMessage('Main image updated successfully');
      handleCloseCropDialog();
    } catch (error) {
      console.error('Error uploading image:', error);
      setError('Failed to upload image');
    } finally {
      setIsImageUploading(false);
    }
  };

  const handleDeleteImage = async () => {
    try {
      await axios.delete('/api/v2/settings/main-image');
      setMainImage(null);
      setSuccessMessage('Main image deleted successfully');
    } catch (error) {
      console.error('Error deleting image:', error);
      setError('Failed to delete image');
    }
  };

  // Crop Dialog Component
  const renderCropDialog = () => (
    <Dialog 
      open={cropDialogOpen} 
      onClose={handleCloseCropDialog}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Crop Image</DialogTitle>
      <DialogContent>
        <Box sx={{ width: '100%', height: '100%', minHeight: 400, position: 'relative' }}>
          {imageSrc && (
            <ReactCrop
              crop={crop}
              onChange={(c) => {
                setCrop(c);
              }}
              onComplete={(c) => {
                setCompletedCrop(c);
              }}
              aspect={16 / 9}
            >
              <img
                ref={imageRef}
                alt="Crop"
                src={imageSrc}
                style={{ maxWidth: '100%', maxHeight: '70vh' }}
                onLoad={onImageLoad}
              />
            </ReactCrop>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseCropDialog} color="inherit">Cancel</Button>
        <Button 
          onClick={handleUploadCroppedImage}
          variant="contained"
          color="primary"
          disabled={isImageUploading || !completedCrop?.width || !completedCrop?.height}
        >
          {isImageUploading ? 'Uploading...' : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const renderDashboard = () => (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            p: 3,
            borderRadius: 2,
            bgcolor: 'primary.light',
            color: 'white',
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Person sx={{ fontSize: 40, mr: 2 }} />
            <Typography variant="h4">
              {stats.activeClients || 0}
            </Typography>
          </Box>
          <Typography variant="subtitle1">Active Clients</Typography>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            flex: 1,
            p: 3,
            borderRadius: 2,
            bgcolor: 'secondary.light',
            color: 'white',
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <MonetizationOn sx={{ fontSize: 40, mr: 2 }} />
            <Typography variant="h4">
              ${stats.monthlyIncome ? stats.monthlyIncome.toLocaleString() : '0'}
            </Typography>
          </Box>
          <Typography variant="subtitle1">Monthly Income</Typography>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            flex: 1,
            p: 3,
            borderRadius: 2,
            bgcolor: 'success.light',
            color: 'white',
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Schedule sx={{ fontSize: 40, mr: 2 }} />
            <Typography variant="h4">
              {stats.upcomingSessions || 0}
            </Typography>
          </Box>
          <Typography variant="subtitle1">Upcoming Sessions</Typography>
        </Paper>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} md={7}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              height: '100%',
              transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
              },
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
              Upcoming Sessions
            </Typography>
            {upcomingSessions && upcomingSessions.length > 0 ? (
              <Box sx={{ mt: 1 }}>
                {upcomingSessions.map((session) => (
                  <Paper 
                    key={session._id} 
                    sx={{ 
                      p: 2, 
                      mb: 2,
                      borderRadius: 2,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)'
                      }
                    }}
                  >
                    <Typography variant="h6" sx={{ 
                      fontWeight: 600,
                      color: 'text.primary',
                      mb: 0.5
                    }}>
                      {session.sessionTitle}
                    </Typography>
                    <Box sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      mb: 1
                    }}>
                      <CalendarToday fontSize="small" sx={{ color: 'text.secondary', fontSize: '0.9rem' }} />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {format(new Date(session.date), 'MMM d, yyyy')} at {session.time}
                      </Typography>
                      {session.trainer && (
                        <>
                          <Typography variant="body2" sx={{ color: 'text.secondary', mx: 0.5 }}>•</Typography>
                          <SupervisorAccount fontSize="small" sx={{ color: 'text.secondary', fontSize: '0.9rem' }} />
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Coach {session.trainer.name}
                          </Typography>
                        </>
                      )}
                    </Box>
                    <Box sx={{ 
                      display: 'flex',
                      gap: 1,
                      mb: 1.5
                    }}>
                      <Chip 
                        label={`${session.currentParticipants}/${session.maxParticipants} Enrolled`}
                        color={session.remainingSpots > 0 ? "primary" : "error"}
                        variant="outlined"
                        size="small"
                        sx={{ height: '24px' }}
                      />
                      <Chip 
                        label={session.sessionType}
                        color="secondary"
                        variant="outlined"
                        size="small"
                        sx={{ height: '24px' }}
                      />
                    </Box>
                    {session.enrolledClients?.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" sx={{ 
                          fontWeight: 600,
                          color: 'text.secondary',
                          mb: 0.5
                        }}>
                          Enrolled Players:
                        </Typography>
                        <Box sx={{ pl: 1 }}>
                          {session.enrolledClients.map((client, idx) => (
                            <Typography 
                              key={idx} 
                              variant="body2"
                              sx={{ 
                                py: 0.25,
                                color: client.status === 'pending' ? 'error.main' : 'text.primary',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                              }}
                            >
                              <Person fontSize="small" sx={{ 
                                color: client.status === 'pending' ? 'error.main' : 'primary.main', 
                                fontSize: '0.9rem' 
                              }} />
                              {client.name}
                              {client.status === 'pending' && (
                                <Button
                                  size="small"
                                  startIcon={<AttachMoney />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleConfirmPayment(client._id);
                                  }}
                                  sx={{
                                    ml: 1,
                                    color: 'error.main',
                                    '&:hover': {
                                      backgroundColor: 'rgba(211, 47, 47, 0.08)'
                                    }
                                  }}
                                >
                                  Confirm Payment
                                </Button>
                              )}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    )}
                    {!session.enrolledClients?.length && (
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                        No players enrolled
                      </Typography>
                    )}
                  </Paper>
                ))}
              </Box>
            ) : (
              <Typography 
                variant="body2"
                color="text.secondary" 
                sx={{ 
                  textAlign: 'center',
                  fontStyle: 'italic',
                  py: 3
                }}
              >
                No upcoming sessions scheduled
              </Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              borderRadius: 2,
              height: '100%',
              '& .MuiPickersDay-root': {
                borderRadius: 1,
              },
              '& .MuiPickersDay-today': {
                borderColor: 'primary.main',
              },
            }}
          >
            <Typography variant="h6" gutterBottom>
              Calendar
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateCalendar
                value={selectedDate}
                onChange={(newDate) => setSelectedDate(newDate)}
                sx={{ width: '100%' }}
              />
            </LocalizationProvider>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );

  const handleCreateNewPost = () => {
    // Using cookie-based auth now, no need to handle tokens manually
    navigate('/admin/blog/new');
  };

  const renderBlogPosts = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" color="primary.main">
          Blog Posts
        </Typography>
        <Button
          variant="contained"
          startIcon={<PostAdd />}
          onClick={handleCreateNewPost}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            px: 3,
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          Create New Post
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <List sx={{ bgcolor: 'background.paper', borderRadius: 2, overflow: 'hidden' }}>
          {blogPosts.map((blog, index) => (
            <React.Fragment key={blog._id}>
              {index > 0 && <Divider />}
              <ListItem
                sx={{
                  py: 2,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    bgcolor: 'action.hover',
                    transform: 'translateX(8px)',
                  },
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    src={blog.image}
                    variant="rounded"
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: 2,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    }}
                  >
                    <PostAdd />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 500 }}>
                      {blog.title}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {new Date(blog.createdAt).toLocaleDateString()}
                    </Typography>
                  }
                  sx={{ ml: 2 }}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    color="primary"
                    onClick={() => handleEditBlog(blog._id)}
                    sx={{
                      color: 'primary.main',
                      '&:hover': {
                        bgcolor: 'primary.lighter',
                      },
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    color="error"
                    onClick={() => handleDeleteClick(blog)}
                    sx={{
                      color: 'error.main',
                      '&:hover': {
                        bgcolor: 'error.lighter',
                      },
                    }}
                  >
                    Delete
                  </Button>
                </Box>
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      )}
    </Box>
  );

  const getCroppedImg = async (image, crop) => {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext('2d');

    const pixelRatio = window.devicePixelRatio;
    canvas.width = crop.width * pixelRatio;
    canvas.height = crop.height * pixelRatio;

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width,
      crop.height
    );

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            console.error('Canvas is empty');
            return;
          }
          blob.name = 'cropped.jpg';
          resolve(blob);
        },
        'image/jpeg',
        1
      );
    });
  };

  const handleSaveCrop = async () => {
    try {
      if (!completedCrop || !imageRef.current) {
        console.error('No crop or image ref');
        return;
      }

      setIsImageUploading(true);
      const croppedImage = await getCroppedImg(
        imageRef.current,
        completedCrop
      );

      if (!croppedImage) {
        console.error('Failed to create cropped image');
        return;
      }

      const formData = new FormData();
      formData.append('image', croppedImage, 'cropped.jpg');

      const response = await axios.put('/api/v2/settings/main-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setMainImage(response.data);
      setSuccessMessage('Main image updated successfully');
      setCropDialogOpen(false);
      setImageSrc(null);
      setCrop({
        unit: '%',
        x: 0,
        y: 0,
        width: 90,
        height: 90
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      setError('Failed to update main image');
    } finally {
      setIsImageUploading(false);
    }
  };

  const handleDeleteMainImage = async () => {
    try {
      await axios.delete('/api/v2/settings/main-image');
      setMainImage(null);
      setSuccessMessage('Main image deleted successfully');
    } catch (error) {
      console.error('Error deleting main image:', error);
      setError('Failed to delete main image');
    }
  };

  const renderMainPage = () => {
    return (
      <Box>
        <Typography variant="h5" gutterBottom sx={{ 
          fontWeight: 600,
          color: 'primary.main',
          mb: 3
        }}>
          Main Page Settings
        </Typography>

        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
          <Box sx={{ mb: 4 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2
            }}>
              <Typography variant="h6" sx={{ color: 'text.primary' }}>
                Main Page Content
              </Typography>
              {!isEditingText && (
                <Button
                  variant="outlined"
                  onClick={() => setIsEditingText(true)}
                  startIcon={<Edit />}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none'
                  }}
                >
                  Edit Content
                </Button>
              )}
            </Box>
            
            {isEditingText ? (
              <Box sx={{ 
                bgcolor: 'background.paper',
                borderRadius: 1,
                p: 3,
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <TextField
                  fullWidth
                  label="Page Title"
                  value={editedSettings.title}
                  onChange={(e) => setEditedSettings({ ...editedSettings, title: e.target.value })}
                  sx={{ mb: 3 }}
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label="Page Subtitle"
                  value={editedSettings.subtitle}
                  onChange={(e) => setEditedSettings({ ...editedSettings, subtitle: e.target.value })}
                  multiline
                  rows={4}
                  sx={{ mb: 3 }}
                  variant="outlined"
                />
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={handleCancelEdit}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none'
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSaveText}
                    startIcon={<Save />}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none'
                    }}
                  >
                    Save Changes
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box sx={{ 
                bgcolor: 'background.paper',
                borderRadius: 1,
                p: 3,
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Page Title
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 500 }}>
                    {mainPageSettings.title}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Page Subtitle
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    whiteSpace: 'pre-wrap',
                    fontStyle: 'italic',
                    color: 'text.secondary'
                  }}>
                    {mainPageSettings.subtitle}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Hero Image</Typography>
            {mainImage ? (
              <>
                <Box
                  component="img"
                  src={mainImage.url}
                  alt="Main Image"
                  sx={{
                    width: '100%',
                    maxHeight: 300,
                    objectFit: 'cover',
                    borderRadius: 1,
                    mb: 2
                  }}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<PhotoLibrary />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isImageUploading}
                  >
                    Change Image
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Delete />}
                    onClick={handleDeleteImage}
                  >
                    Delete Image
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <Typography sx={{ mb: 2 }}>No hero image uploaded</Typography>
                <Button
                  variant="contained"
                  startIcon={<PhotoLibrary />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isImageUploading}
                >
                  Upload New Image
                </Button>
              </>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              style={{ display: 'none' }}
              ref={fileInputRef}
            />
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Tip: You can also paste an image directly using Ctrl+V
            </Typography>
          </Box>
        </Paper>
      </Box>
    );
  };

  const handleDeleteClick = (blog) => {
    setBlogToDelete(blog);
    setDeleteDialogOpen(true);
  };

  const handleDeleteClose = () => {
    setBlogToDelete(null);
    setDeleteDialogOpen(false);
    setDeleteError('');
  };

  const handleDeleteConfirm = async () => {
    if (!blogToDelete) return;
    
    setIsDeleting(true);
    setDeleteError('');
    
    try {
      await axios.delete(`/api/v2/blog/${blogToDelete._id}`);
      setBlogPosts(blogPosts.filter(blog => blog._id !== blogToDelete._id));
      setSuccessMessage('Blog post deleted successfully');
      handleDeleteClose();
    } catch (err) {
      console.error('Error deleting blog post:', err);
      setDeleteError('Failed to delete blog post. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditBlog = (blogId) => {
    navigate(`/admin/blog/edit/${blogId}`);
  };

  const renderDeleteDialog = () => (
    <Dialog
      open={deleteDialogOpen}
      onClose={handleDeleteClose}
      aria-labelledby="delete-dialog-title"
    >
      <DialogTitle id="delete-dialog-title">Delete Blog Post</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to delete "{blogToDelete?.title}"? This action cannot be undone.
        </DialogContentText>
        {deleteError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {deleteError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDeleteClose} disabled={isDeleting}>
          Cancel
        </Button>
        <Button 
          onClick={handleDeleteConfirm} 
          color="error" 
          disabled={isDeleting}
          startIcon={isDeleting ? <CircularProgress size={20} /> : null}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );

  const fetchBlogPosts = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('authToken');
      console.log('fetchBlogPosts: Using token:', token ? 'exists' : 'missing');

      const axiosConfig = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };

      const response = await axios.get('/api/v2/blog/admin', axiosConfig);
      
      console.log('fetchBlogPosts: Response:', response.data);
      setBlogPosts(response.data.blogs || response.data || []);
    } catch (err) {
      console.error('Error fetching blog posts:', err);
      if (err.response?.status === 401) {
        console.log('fetchBlogPosts: Unauthorized, redirecting to login');
        navigate('/admin');
      } else {
        setError('Failed to fetch blog posts');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderClients = () => {
    // Early return with loading state if data is not ready
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      );
    }

    // Ensure activeClients is an array and has data
    const clients = Array.isArray(activeClients) ? activeClients : [];
    
    // Map the raw data to row format
    const rows = clients.map(client => {
      // Ensure each field exists and has a value
      const row = {
        id: client?._id || `temp-${Math.random()}`,
        _id: client?._id || '',
        playerName: client?.playerName || '',
        parentName: client?.parentName || '',
        email: client?.email || '',
        phone: client?.phone || '',
        createdAt: client?.createdAt || new Date()
      };
      console.log('Created row:', row);
      return row;
    });

    const columns = [
      {
        field: 'playerName',
        headerName: 'Player',
        flex: 1,
        minWidth: 150,
        renderCell: (params) => (
          <Typography sx={{ fontWeight: 500 }}>
            {params.row.playerName || 'N/A'}
          </Typography>
        )
      },
      {
        field: 'parentName',
        headerName: 'Primary Contact',
        flex: 1,
        minWidth: 150,
        renderCell: (params) => (
          <Typography>
            {params.row.parentName || 'N/A'}
          </Typography>
        )
      },
      {
        field: 'email',
        headerName: 'Email',
        flex: 1,
        minWidth: 200,
        renderCell: (params) => (
          <Typography sx={{ color: 'text.secondary' }}>
            {params.row.email || 'N/A'}
          </Typography>
        )
      },
      {
        field: 'phone',
        headerName: 'Phone',
        flex: 1,
        minWidth: 120,
        renderCell: (params) => (
          <Typography sx={{ color: 'text.secondary' }}>
            {params.row.phone || 'N/A'}
          </Typography>
        )
      },
      {
        field: 'createdAt',
        headerName: 'Joined',
        flex: 1,
        minWidth: 120,
        renderCell: (params) => (
          <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
            {params.row.createdAt ? format(new Date(params.row.createdAt), 'MMM dd, yyyy') : 'N/A'}
          </Typography>
        )
      },
      {
        field: 'actions',
        headerName: 'Actions',
        flex: 1,
        minWidth: 120,
        sortable: false,
        renderCell: (params) => (
          <Button
            size="small"
            startIcon={<Event />}
            onClick={(e) => {
              e.stopPropagation();
              handleViewBookings(params.row._id);
            }}
            sx={{
              color: 'primary.main',
              '&:hover': {
                backgroundColor: 'rgba(63, 81, 181, 0.08)'
              }
            }}
          >
            Bookings
          </Button>
        )
      }
    ];

    return (
      <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Typography 
          variant="h5" 
          color="primary.main" 
          sx={{ 
            mb: 3,
            fontWeight: 600,
            letterSpacing: '0.5px'
          }}
        >
          Client Management
        </Typography>

        <Paper
          elevation={0}
          sx={{
            flex: 1,
            width: '100%',
            minHeight: 400,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            overflow: 'hidden',
            '& .MuiDataGrid-root': {
              border: 'none',
              flex: 1,
              fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
              fontSize: '0.875rem',
              letterSpacing: '0.15px'
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
              padding: '12px 16px',
              color: 'rgba(0, 0, 0, 0.87)'
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'rgba(0, 0, 0, 0.02)',
              borderBottom: '2px solid rgba(224, 224, 224, 1)',
              fontWeight: 600,
              fontSize: '0.875rem',
              letterSpacing: '0.5px',
              textTransform: 'uppercase'
            },
            '& .MuiDataGrid-virtualScroller': {
              backgroundColor: 'white'
            },
            '& .MuiDataGrid-main': {
              flex: 1,
              width: '100%'
            },
            '& .MuiButton-root': {
              textTransform: 'none',
              fontWeight: 500
            }
          }}
        >
          <DataGrid
            rows={rows}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            getRowId={(row) => row._id}
            components={{
              NoRowsOverlay: () => (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 2 }}>
                  <Typography variant="body1" color="text.secondary">
                    No clients found
                  </Typography>
                </Box>
              ),
              LoadingOverlay: () => (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 2 }}>
                  <CircularProgress />
                </Box>
              )
            }}
            sx={{
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          />
        </Paper>
        {renderBookingsModal()}
      </Box>
    );
  };

  const renderBookingsModal = () => (
    <Dialog
      open={bookingsModalOpen}
      onClose={handleCloseBookingsModal}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Client Bookings</DialogTitle>
      <DialogContent>
        {bookingsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Session</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedClientBookings.length > 0 ? (
                  selectedClientBookings.map((booking) => (
                    <TableRow key={booking._id}>
                      <TableCell>
                        {format(new Date(booking.createdAt), 'MMM dd, yyyy')}
                      </TableCell>
                      <TableCell>{booking.session?.title || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip 
                          label={booking.status} 
                          color={booking.status === 'confirmed' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        ${(booking.amount / 100).toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          startIcon={<AttachMoney />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleConfirmPayment(booking._id);
                          }}
                          sx={{
                            color: 'primary.main',
                            '&:hover': {
                              backgroundColor: 'rgba(63, 81, 181, 0.08)'
                            }
                          }}
                        >
                          Confirm Payment
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No bookings found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseBookingsModal} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderTrainerManagement = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" color="primary.main">
          Trainer Management
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          bgcolor: 'background.paper',
          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          },
        }}
      >
        <TrainerManagement />
      </Paper>
    </Box>
  );

  const renderTabContent = () => {
    switch (selectedTab) {
      case 0:
        return renderDashboard();
      case 1:
        return <TrainingManagement />;
      case 2:
        return renderBlogPosts();
      case 3:
        return <ManageGallery />;
      case 4:
        return renderClients();
      case 5:
        return <TrainerManagement />;
      case 6:
        return <TrainingPathManagement />;
      case 7:
        return renderMainPage();
      case 8:
        return <TestimonialManagement />;
      case 9:
        return <AdminManagement />;
      default:
        return renderDashboard();
    }
  };

  const tabs = [
    { label: 'Dashboard', icon: <Event /> },
    { label: 'Training Sessions', icon: <Schedule /> },
    { label: 'Blog Posts', icon: <PostAdd /> },
    { label: 'Gallery', icon: <PhotoLibrary /> },
    { label: 'Clients', icon: <Person /> },
    { label: 'Trainer Management', icon: <SupervisorAccount /> },
    { label: 'Training Paths', icon: <Route /> },
    { label: 'Main Page', icon: <PhotoLibrary /> },
    { label: 'Testimonials', icon: <FormatQuote /> }
  ];

  if (isSuperAdmin) {
    tabs.push({ label: 'Admin Management', icon: <AdminPanelSettings /> });
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static" color="default">
        <Toolbar>
          <Typography variant="h5" component="h1" sx={{ flexGrow: 1, color: 'primary.main' }}>
            Admin Dashboard
          </Typography>
          <IconButton
            size="large"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenuClick}
            sx={{
              ml: 1,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                transform: 'scale(1.1)',
                transition: 'all 0.2s ease-in-out',
              },
            }}
          >
            <AccountCircle sx={{ fontSize: 32, color: 'primary.main' }} />
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: {
                mt: 1.5,
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                borderRadius: '12px',
                minWidth: '200px',
                '& .MuiList-root': {
                  padding: '8px',
                },
              },
            }}
          >
            <Box sx={{ p: 2, borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Admin Account
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Manage your account
              </Typography>
            </Box>
            <MenuItem
              onClick={handleLogout}
              sx={{
                borderRadius: '8px',
                m: 1,
                py: 1,
                '&:hover': {
                  backgroundColor: 'error.lighter',
                  '& .MuiSvgIcon-root': {
                    color: 'error.main',
                  },
                  '& .MuiTypography-root': {
                    color: 'error.main',
                  },
                },
              }}
            >
              <ExitToApp sx={{ mr: 2, color: 'text.secondary' }} />
              <Typography>Logout</Typography>
            </MenuItem>
          </Menu>
        </Toolbar>
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="dashboard tabs"
        >
          {tabs.map((tab, index) => (
            <Tab key={index} icon={tab.icon} label={tab.label} />
          ))}
        </Tabs>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {renderCropDialog()}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        ) : (
          renderTabContent()
        )}

        {renderDeleteDialog()}

        {/* Success Message Snackbar */}
        <Snackbar
          open={!!successMessage}
          autoHideDuration={6000}
          onClose={() => setSuccessMessage('')}
          message={successMessage}
        />
      </Container>
    </Box>
  );
};

export default Dashboard;
